import { mdiAirplaneCheck, mdiAirplanePlus, mdiCommentOutline, mdiPencil } from '@mdi/js'
import Icon from '@mdi/react'
import { noop } from 'lodash'
import React, { useMemo } from 'react'
import Select from 'react-select'

import {
    useBulkAssignPASSubjectMutation,
    useExportPASHtmlMutation,
    useExportPASPdfMutation,
    useExportPASXlsxMutation,
    useListPASQuery,
} from '../../api/for-company/pas.api'
import { useAssignedProject } from '../../api/for-company/projects.api'
import { Badge } from '../../atoms/Badge'
import { EmptyResponse } from '../../atoms/EmptyResponse'
import { styles } from '../../atoms/EntitySelector'
import { ExportHtmlButton } from '../../atoms/Export/ExportHtmlButton'
import { ExportPdfButton } from '../../atoms/Export/ExportPdfButton'
import { ExportXlsButton } from '../../atoms/Export/ExportXlsButton'
import { IconButton } from '../../atoms/IconButton'
import { Menu, MenuItem } from '../../atoms/Menu'
import { Modal } from '../../atoms/Modal/Modal'
import { DefaultCellValue } from '../../atoms/Table/DefaultCell'
import { Table, TableColumn } from '../../atoms/Table/Table'
import { Tooltip } from '../../atoms/Tooltip'
import { PAS, PASStatus } from '../../domain/PAS.domain'
import { ProjectRolePermissions } from '../../domain/project-role.domain'
import { Project, ProjectSubject } from '../../domain/project.domain'
import { openFile } from '../../lib/open-file'
import { ForProjectRole } from '../Auth/ForProjectRole'

import { AddPASSubpart } from './AddPASSubpart'
import { EditPASEntry } from './EditPAS'
import { PASStatusView } from './PASStatusView'

const getPASColumns = ({
    projectId,
    subjectId,
}: {
    projectId: number
    subjectId: number
    // eslint-disable-next-line @typescript-eslint/ban-types
}): TableColumn<PAS, {}>[] => [
    {
        id: 'menu',
        Header: () => {
            const [bulkAssign] = useBulkAssignPASSubjectMutation()
            const project = useAssignedProject({ projectId })

            if (!project) {
                return null
            }

            return (
                <Menu
                    body={() => (
                        <Tooltip text='Menu' className='justify-center'>
                            <Icon
                                className='text-atamblue-300 hover:text-atamsky-900 hover:rotate-0'
                                path={mdiAirplaneCheck}
                                size={0.75}
                            />
                        </Tooltip>
                    )}
                    className='bg-ebony-100 bg-opacity-90'
                    render={({ close }) => (
                        <>
                            <ForProjectRole
                                permission={ProjectRolePermissions.PASListSubparts}
                                projectId={projectId}
                            >
                                <MenuItem icon={mdiAirplanePlus}>
                                    Bulk assign:{' '}
                                    <Select<{ id: number; name: string }>
                                        value={undefined}
                                        options={project.assignments}
                                        getOptionLabel={user => user.name}
                                        getOptionValue={user => `${user.id}`}
                                        styles={styles as any}
                                        className='w-64'
                                        onChange={user => {
                                            if (!user) {
                                                close()
                                                return
                                            }
                                            bulkAssign({
                                                projectId,
                                                subjectId,
                                                userId: user.id,
                                            }).then(close)
                                        }}
                                    />
                                </MenuItem>
                            </ForProjectRole>
                        </>
                    )}
                />
            )
        },
        Cell: ({ value }) => (
            <DefaultCellValue>
                <Modal
                    modalBodyClassName='overflow-hidden'
                    body={({ close }) => (
                        <EditPASEntry
                            projectId={projectId}
                            subjectId={subjectId}
                            pas={value}
                            onSave={close}
                            onCancel={close}
                        />
                    )}
                >
                    <IconButton path={mdiPencil} size={0.75} />
                </Modal>
            </DefaultCellValue>
        ),
        width: 4,
    },
    {
        id: 'subpart-name',
        Header: () => <div className='py-2 text-left'>Subpart Name</div>,
        Cell: ({ value }) => <DefaultCellValue align='left'>{value.name}</DefaultCellValue>,
        width: 25,
    },
    {
        id: 'category-name',
        Header: () => <div className='py-2'>Category Name</div>,
        Cell: ({ value }) => <DefaultCellValue>{value.category.name}</DefaultCellValue>,
        width: 15,
    },
    {
        id: 'status',
        Header: () => <div className='py-2'>Status</div>,
        Cell: ({ value }) => {
            return (
                <DefaultCellValue>
                    <PASStatusView status={value.status} short />
                </DefaultCellValue>
            )
        },
        width: 15,
    },
    {
        id: 'poc',
        Header: () => <div className='py-2'>POC</div>,
        Cell: ({ value }) => (
            <DefaultCellValue>
                {value.status === PASStatus.NotApplicable ? '-' : `${value.poc}%`}
            </DefaultCellValue>
        ),
        width: 5,
    },
    {
        id: 'asignee',
        Header: () => <div className='py-2'>Assigned To</div>,
        Cell: ({ value }) => (
            <DefaultCellValue>
                {value.status === PASStatus.NotApplicable ? '-' : `${value.user?.code ?? '-'}`}
            </DefaultCellValue>
        ),
        width: 10,
    },
    {
        id: 'remarks',
        Header: () => <div className='py-2'>Remarks</div>,
        Cell: ({ value }) => {
            const existingRemarks = value.remarks.filter(remark => !remark.isDeleted)
            return (
                <DefaultCellValue align='left'>
                    <div className='flex items-center py-1 truncate text-ellipsis overflow-hidden'>
                        <Badge
                            icon={mdiCommentOutline}
                            className='text-atamblue-300'
                            contentClassName='pl-1 bg-white text-xs'
                        >
                            {existingRemarks.length}
                        </Badge>
                        <div className='ml-3'>
                            {existingRemarks.length > 0
                                ? existingRemarks[existingRemarks.length - 1].content
                                : null}
                        </div>
                    </div>
                </DefaultCellValue>
            )
        },
        width: 25,
    },
]

export const ListPAS: React.FC<{
    projectId: Project['id']
    subjectId: ProjectSubject['id']
}> = props => {
    const { projectId, subjectId } = props
    const { data } = useListPASQuery({ projectId: props.projectId, subjectId: props.subjectId })

    const [exportXlsx, exportXlsxMeta] = useExportPASXlsxMutation()
    const [exportPdf, exportPdfMeta] = useExportPASPdfMutation()
    const [exportHtml, exportHtmlMeta] = useExportPASHtmlMutation()

    const pasColumns = useMemo(() => getPASColumns({ projectId, subjectId }), [
        projectId,
        subjectId,
    ])
    if (!data) {
        return null
    }

    return (
        <>
            <Table
                headerClassName='py-3'
                rowClassName={() => 'py-0.5'}
                columns={pasColumns}
                data={data}
                emptyFallback={() => <EmptyResponse>No subparts</EmptyResponse>}
                tableActions={() => (
                    <div className='flex items-center ml-12 mb-2'>
                        <ForProjectRole
                            permission={ProjectRolePermissions.PASExportXls}
                            projectId={projectId}
                        >
                            <ExportXlsButton
                                isLoading={exportXlsxMeta.isLoading}
                                onClick={() =>
                                    exportXlsx({ projectId, subjectId })
                                        .unwrap()
                                        .then(({ path }) => openFile(path))
                                        .catch(noop)
                                }
                            />
                        </ForProjectRole>
                        <ForProjectRole
                            permission={ProjectRolePermissions.PASExportPdf}
                            projectId={projectId}
                        >
                            <ExportPdfButton
                                isLoading={exportPdfMeta.isLoading}
                                onClick={() =>
                                    exportPdf({ projectId, subjectId })
                                        .unwrap()
                                        .then(({ path }) => openFile(path))
                                        .catch(noop)
                                }
                            />
                        </ForProjectRole>
                        <ForProjectRole
                            permission={ProjectRolePermissions.PASExportStatic}
                            projectId={projectId}
                        >
                            <ExportHtmlButton
                                isLoading={exportHtmlMeta.isLoading}
                                onClick={() =>
                                    exportHtml({ projectId, subjectId })
                                        .unwrap()
                                        .then(({ path }) => openFile(path))
                                        .catch(noop)
                                }
                            />
                        </ForProjectRole>
                    </div>
                )}
            />

            <AddPASSubpart projectId={projectId} subjectId={subjectId} />
        </>
    )
}
